/**
 * ArrivoModal (#91) — transizione programmato → in_transito.
 * Arrivo camion + prima pesata (lordo): FIR, esito radiometrico, peso lordo.
 * Se il radiometrico è KO, si suggerisce di respingere (il respingi è separato).
 */
import { useState } from 'react';
import { Truck, X, AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { inTransito } from '../../api/conferimenti';
import type { ConferimentoDetail } from '../../api/conferimenti';
import i18n from '../../i18n';

const schema = z.object({
  numero_fir: z.string().min(1, i18n.t('conferimenti.arrivo_modal.val_fir')),
  peso_lordo_kg: z.preprocess(
    (v) => (v === '' || v == null ? undefined : Number(v)),
    z.number().int().positive().optional(),
  ),
  controllo_radiometrico_ok: z.boolean().optional(),
});
type FormData = z.infer<typeof schema>;

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

interface Props {
  conferimentoId: string;
  numero: string | null;
  onClose: () => void;
  onSaved: (updated: ConferimentoDetail) => void;
}

export default function ArrivoModal({ conferimentoId, numero, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [serverError, setServerError] = useState<string | null>(null);
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { controllo_radiometrico_ok: true },
  });
  // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch(): il progetto non usa il React Compiler
  const radioOk = watch('controllo_radiometrico_ok');

  async function onSubmit(data: FormData) {
    setServerError(null);
    try {
      const updated = await inTransito(conferimentoId, {
        numero_fir: data.numero_fir,
        controllo_radiometrico_ok: data.controllo_radiometrico_ok,
        peso_lordo_kg: data.peso_lordo_kg,
      });
      onSaved(updated);
    } catch {
      setServerError(t('conferimenti.arrivo_modal.errore_salvataggio'));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <Truck size={16} className="text-sage" /> {t('conferimenti.arrivo_modal.titolo')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit((d) => onSubmit(d as FormData))}>
          <div className="px-6 py-5 space-y-4">
            <p className="text-xs text-ink-soft"><Trans i18nKey="conferimenti.arrivo_modal.conferimento_label" values={{ numero: numero ?? '—' }} components={{ strong: <strong /> }} /></p>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.arrivo_modal.fir')}</label>
              <input type="text" className={inputCls} placeholder={t('conferimenti.arrivo_modal.fir_placeholder')} {...register('numero_fir')} />
              {errors.numero_fir && <p className="text-xs text-accent-danger mt-1">{errors.numero_fir.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.arrivo_modal.peso_lordo')}</label>
              <input type="number" step="0.01" className={inputCls} placeholder="0" {...register('peso_lordo_kg')} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="radiometrico" className="w-4 h-4 text-sage rounded border-stone-border" {...register('controllo_radiometrico_ok')} />
              <label htmlFor="radiometrico" className="text-sm text-ink-mid">{t('conferimenti.arrivo_modal.radiometrico_ok')}</label>
            </div>
            {radioOk === false && (
              <div className="flex items-start gap-2 px-3 py-2 bg-accent-warning-bg border border-[#FDE68A] rounded text-xs text-accent-warning">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('conferimenti.arrivo_modal.radio_ko_warning')}</span>
              </div>
            )}
            {serverError && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{serverError}</div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">{t('common.azioni.annulla')}</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60">
              {isSubmitting ? t('conferimenti.arrivo_modal.salvataggio') : t('conferimenti.arrivo_modal.registra')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
